/**
 * GoLivePanel — the Go-live step. Before: a summary of what will be created
 * and a primary "Go live" button. After, leading with what matters:
 *
 *   - success (bound ports, config pushed) and the live values of the bound
 *     ports (twinBindings.portValues, polled) so the operator sees data arrive;
 *   - next actions: "View on gateway" (the gateway page, its Devices tab),
 *     "Connect another device", "View equipment", and "Test read"
 *     (things.measure: one immediate read of the device's points);
 *   - "Details", collapsed: the source slug and the slug-equality note
 *     (real-device-onboarding.md §slug: the source Thing slug MUST equal the
 *     gateway device name or readings orphan silently), and the equipment's
 *     operational state (EquipmentStatePanel, with its backfill).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CircleCheck, Gauge, Loader2, Plus, RadioTower, TriangleAlert, Wifi } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/lib/ui/card";
import { Disclosure } from "@/lib/ui/disclosure";
import { Skeleton } from "@/lib/ui/skeleton";
import { useToast } from "@/lib/ui/toast";
import { EquipmentStatePanel } from "@/components/twin/EquipmentStatePanel";
import { isLiveApi, things, twinBindings } from "@/lib/api";
import { portFreshness } from "@/lib/port-freshness";
import { deviceAddressLabel, type DiscoveredDevice } from "@/lib/gateway-client";
import type { CommitResult, RefusedPoint } from "./commit";
import { gatewayDevicesHref } from "./links";
import type { TagRow } from "@/components/iot/ThingTagRows";

export interface GoLivePanelProps {
  device: DiscoveredDevice;
  /** The protocol picked on the Protocol step, as a label (defaults to the device's driver id). */
  protocolLabel?: string;
  gatewayName: string;
  gatewayId: string | null;
  entityId: string | null;
  entityName: string;
  rows: TagRow[];
  binds: Record<string, string>;
  result: CommitResult | null;
  busy: boolean;
  /** Points the server refused on the last try, each with its reason (fix them on the Map step). */
  refused?: RefusedPoint[];
  canWrite: boolean;
  onGoLive: () => void;
  /** Start the wizard over for the next device (after going live). */
  onConnectAnother?: () => void;
}

function agoLabel(iso?: string | null): string {
  if (!iso || iso.startsWith("0001-01-01")) return "no data yet";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

/** "live · 30s ago" for a fresh reading; "last reading as of 26 Aug 2026" for an imported or older one. */
function freshnessLabel(iso?: string | null): string {
  if (!iso || iso.startsWith("0001-01-01")) return "no data yet";
  const f = portFreshness(iso, Date.now());
  return f.live ? `live · ${agoLabel(iso)}` : `last reading ${f.asOf ?? "at an unknown time"}`;
}

export function GoLivePanel({
  device,
  protocolLabel,
  gatewayName,
  gatewayId,
  entityId,
  entityName,
  rows,
  binds,
  result,
  busy,
  refused = [],
  canWrite,
  onGoLive,
  onConnectAnother,
}: GoLivePanelProps) {
  const mappedCount = rows.filter((r) => r.name.trim() && (r.address.trim() || r.point_address)).length;
  const plannedBinds = Object.keys(binds).length;

  const blockedReason = !canWrite
    ? "You don't have permission to commission devices."
    : !entityId
      ? "Assign the device to a piece of equipment first."
      : isLiveApi && !gatewayId
        ? "Select a gateway on the Discover step first."
        : mappedCount === 0
          ? "Map at least one point first."
          : null;

  if (result) {
    return (
      <LiveResult
        result={result}
        device={device}
        gatewayId={gatewayId}
        gatewayName={gatewayName}
        entityId={entityId}
        entityName={entityName}
        onConnectAnother={onConnectAnother}
      />
    );
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <RadioTower className="size-5 text-brand" />
          Go live
        </CardTitle>
        <CardDescription>
          Creates the device on {gatewayName}, pushes its config and binds it to {entityName || "the chosen equipment"}.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
          <SummaryRow label="Device" value={`${device.name ?? device.ip} · ${deviceAddressLabel(device)}`} mono />
          <SummaryRow label="Protocol" value={protocolLabel ?? device.driver} />
          <SummaryRow label="Gateway" value={gatewayName} />
          <SummaryRow label="Equipment" value={entityName || "—"} />
          <SummaryRow label="Points" value={`${mappedCount} mapped`} />
          <SummaryRow label="Bindings" value={`${plannedBinds} planned`} />
        </dl>
        <div className="flex flex-col gap-2">
          <Button size="lg" onClick={onGoLive} disabled={busy || !!blockedReason} className="self-start">
            {busy ? <Loader2 className="animate-spin" /> : <RadioTower />}
            {busy ? "Going live…" : "Go live"}
          </Button>
          {blockedReason && <p className="text-xs text-warning">{blockedReason}</p>}
        </div>
        {refused.length > 0 && (
          <section
            role="alert"
            aria-label="Points the server refused"
            className="flex flex-col gap-1.5 rounded-md border border-danger/40 bg-danger/5 p-3 text-xs"
          >
            <span className="flex items-center gap-1.5 font-medium text-on-surface">
              <TriangleAlert className="size-3.5 text-danger" />
              The server refused {refused.length === 1 ? "this point" : `these ${refused.length} points`}. Fix{" "}
              {refused.length === 1 ? "it" : "them"} on the Map step (or the Points step) and try again.
            </span>
            <ul className="flex flex-col gap-1">
              {refused.map((p) => (
                <li key={p.key} className="flex flex-wrap gap-x-2">
                  <span className="font-mono text-on-surface">{p.name}</span>
                  <span className="text-on-surface-muted">{p.message}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-on-surface-muted">{label}</dt>
      <dd className={mono ? "truncate font-mono text-xs text-on-surface" : "truncate text-on-surface"}>{value}</dd>
    </div>
  );
}

function LiveResult({
  result,
  device,
  gatewayId,
  gatewayName,
  entityId,
  entityName,
  onConnectAnother,
}: {
  result: CommitResult;
  device: DiscoveredDevice;
  gatewayId: string | null;
  gatewayName: string;
  entityId: string | null;
  entityName: string;
  onConnectAnother?: () => void;
}) {
  const toast = useToast();
  const [measuring, setMeasuring] = useState(false);
  const deviceName = device.name ?? device.ip;
  const partial = result.bound < result.planned;

  const testRead = async () => {
    setMeasuring(true);
    try {
      if (isLiveApi && gatewayId) {
        await things.measure(gatewayId, { device_id: result.slug });
        toast.push("ok", "Read requested — watch the values update.");
      } else {
        toast.push("info", "Simulated read requested (offline demo).");
      }
    } catch (e) {
      toast.push("error", e instanceof Error ? e.message : "Couldn't request a read.");
    } finally {
      setMeasuring(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="border-success/40">
        <CardContent className="flex flex-col gap-4 p-5">
          <div role="status" className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
              <CircleCheck className="size-6" />
            </div>
            <div className="flex min-w-0 flex-col gap-1">
              <h2 className="text-lg font-semibold text-on-surface">
                {result.simulated ? "Simulated go-live" : "Device is live"}
              </h2>
              <p className="text-sm text-on-surface-muted">
                {deviceName} feeds {entityName || "the equipment"} through {gatewayName}.
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant={partial ? "warning" : "success"} className="text-[10px]">
                  {result.bound}/{result.planned} ports bound
                </Badge>
                {result.configPushed && (
                  <Badge variant="secondary" className="text-[10px]">
                    config pushed
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {partial && (
            <p className="flex items-start gap-2 text-xs text-warning">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              {result.planned - result.bound} port{result.planned - result.bound === 1 ? "" : "s"} kept the previous
              source. Bind {result.planned - result.bound === 1 ? "it" : "them"} from the equipment page.
            </p>
          )}

          {entityId && !result.simulated && <LivePortValues entityId={entityId} />}

          <div className="flex flex-wrap items-center gap-2">
            {gatewayId && (
              <Button asChild>
                <Link to={gatewayDevicesHref(gatewayId)}>
                  View on gateway
                  <ArrowRight />
                </Link>
              </Button>
            )}
            {onConnectAnother && (
              <Button variant="outline" onClick={onConnectAnother}>
                <Plus />
                Connect another device
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/app/twin/equipment">View equipment</Link>
            </Button>
            <Button variant="ghost" onClick={testRead} disabled={measuring}>
              {measuring ? <Loader2 className="animate-spin" /> : <Gauge />}
              Test read
            </Button>
          </div>
        </CardContent>
      </Card>

      <Disclosure
        summary="Details"
        hint="source slug, operational state"
        variant="card"
        contentClassName="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-on-surface-muted">
            <span>Source slug</span>
            <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-on-surface">{result.slug}</code>
          </div>
          <p className="text-xs text-on-surface-muted">
            If no values arrive, check the gateway&apos;s device name equals{" "}
            <span className="font-mono text-on-surface">{result.slug}</span>: readings published under another name
            (than <span className="font-mono text-on-surface">{deviceName}</span>) never reach this source.
          </p>
        </div>
        {entityId && <EquipmentStatePanel entityId={entityId} />}
      </Disclosure>
    </div>
  );
}

function LivePortValues({ entityId }: { entityId: string }) {
  const q = useQuery({
    queryKey: ["connect", "port-values", entityId],
    queryFn: () => twinBindings.portValues(entityId),
    enabled: isLiveApi,
    refetchInterval: 5000,
  });
  const ports = useMemo(() => q.data?.ports ?? [], [q.data]);
  const withFeeds = ports.filter((p) => p.feeds.length > 0);

  return (
    <section aria-label="Live values" className="flex flex-col gap-2 rounded-lg border border-border bg-surface p-3">
      <header className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-on-surface-muted">
        <Wifi className="size-3.5 text-brand" />
        Live values
      </header>
      {q.isLoading ? (
        <div className="flex flex-col gap-1.5" aria-busy="true">
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-3/4" />
        </div>
      ) : q.isError ? (
        <p className="text-sm text-on-surface-muted">Couldn&apos;t read the bound ports. They refresh every 5 s.</p>
      ) : withFeeds.length === 0 ? (
        <p className="text-sm text-on-surface-muted">No readings yet. Use Test read, or wait for the next poll.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {withFeeds.map((p) => (
            <li key={p.port} className="flex items-center gap-2 border-b border-border/60 pb-1.5 text-sm last:border-0">
              <span className="font-medium capitalize text-on-surface">{p.port.replace(/_/g, " ")}</span>
              <div className="ml-auto flex items-center gap-2">
                {p.value != null ? (
                  <>
                    <span className="font-mono text-sm font-semibold text-on-surface">
                      {p.value}
                      {p.unit ? ` ${p.unit}` : ""}
                    </span>
                    <span className="text-[10px] text-on-surface-muted">
                      {/* "live" only for a fresh reading: an imported or older value says how old it is. */}
                      {freshnessLabel(p.time)}
                    </span>
                  </>
                ) : (
                  <span className="text-[10px] text-on-surface-muted">no data yet</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
